import React, { useEffect, useMemo, useState } from 'react';
// 3rd party libraries
import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@apollo/client';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';
// Our Own imports (absolute)
import Heading from '../../atoms/Heading';
import Button from '../../atoms/Button';
import { TrashIcon } from '../../atoms/Icons';
import Select from '../../atoms/Select/Select';
import LoadingSkeleton from '../../atoms/LoadingSkeleton';
import TierSettingsPanel from '../../organisms/TierSettingsPanel/TierSettingsPanel';
import {
  GET_ORG_BARNS_GQL,
  GET_BARN_OVERRIDES_GQL,
  UPSERT_BARN_FAULT_NOTIFICATION_TIER_SETTINGS_GQL,
  DELETE_FAULT_NOTIFICATION_TIER_SETTINGS_GQL,
} from '../OrgManagementPage/queries';
import { NO_NOTIFICATIONS_TIER_NAME } from '../../utils/constants';
import { alertHoursToThresholdSeconds, alertThresholdSecondsToHours } from '../../utils/tierNotificationSettings';

function isEditablePolicyTier(tier) {
  return tier?.editable !== false && tier?.name !== NO_NOTIFICATIONS_TIER_NAME;
}

const BARN_OVERRIDE_SAVED_TOAST_ID = 'barn-override-saved';

function showBarnOverrideSavedToast(message = 'Barn override saved') {
  toast(message, {
    type: 'success',
    toastId: BARN_OVERRIDE_SAVED_TOAST_ID,
    position: 'top-right',
    autoClose: 3000,
    closeOnClick: true,
  });
}

function BarnSettingsOverrideList({ organizationId, planId, orgTierSettings }) {
  // --- State hooks ---
  const [selectedBarnId, setSelectedBarnId] = useState('');
  const [barnOverrides, setBarnOverrides] = useState([]);

  // --- Apollo/GraphQL hooks ---

  const {
    loading: barnOverridesLoading,
    data: barnOverridesData,
    error: barnOverridesError,
  } = useQuery(GET_BARN_OVERRIDES_GQL, {
    variables: {
      org_id: organizationId,
      plan_id: planId,
    },
    skip: !planId,
    fetchPolicy: 'cache-and-network',
    context: { headers: { 'x-hasura-role': 'user' } },
  });

  const {
    loading: barnsLoading,
    data: barnsData,
    error: barnsError,
  } = useQuery(GET_ORG_BARNS_GQL, {
    variables: { organization_id: organizationId },
    skip: !organizationId,
    fetchPolicy: 'cache-first',
    context: { headers: { 'x-hasura-role': 'user' } },
  });

  const [upsertBarnFaultSettings] = useMutation(UPSERT_BARN_FAULT_NOTIFICATION_TIER_SETTINGS_GQL, {
    context: { headers: { 'x-hasura-role': 'user' } },
    onError: (error) => {
      Sentry.captureException(error);
      toast(`Error saving settings: ${error.message}`, { type: 'error' });
    },
  });

  const [deleteFaultSettings] = useMutation(DELETE_FAULT_NOTIFICATION_TIER_SETTINGS_GQL, {
    context: { headers: { 'x-hasura-role': 'user' } },
    onError: (error) => {
      Sentry.captureException(error);
      toast(`Error reverting to organization defaults: ${error.message}`, { type: 'error' });
    },
  });

  // --- Derived state ---
  const availableBarns = barnsData?.farm || [];

  const transformedBarnSettings = useMemo(() => {
    if (!barnOverridesData?.farm) return [];

    return barnOverridesData.farm.reduce((acc, barn) => {
      const tierSettings = {};

      barn.fault_notification_settings
        ?.filter((setting) => isEditablePolicyTier(setting.tier))
        .forEach((setting) => {
          const tierId = setting.tier.id;

          if (!tierSettings[tierId]) {
            tierSettings[tierId] = {
              name: setting.tier.name,
              description: setting.tier.description,
              tierId: tierId,
              sort_order: setting.tier.sort_order,
              faultSettings: {},
            };
          }

          // Add fault setting to the dictionary with fault code as key
          tierSettings[tierId].faultSettings[setting.code.toString()] = {
            code: setting.code,
            alert: {
              enabled: setting.enabled,
              hours: alertThresholdSecondsToHours(setting.alert_threshold_seconds, setting.code),
              type: setting.alert_threshold_type,
            },
            reminders: {
              enabled: setting.renotify_enabled,
              hours: setting.renotify_threshold_seconds / 3600,
            },
            resolved: {
              enabled: setting.notify_on_resolved,
            },
          };
        });

      if (Object.keys(tierSettings).length === 0) {
        return acc;
      }

      acc.push({
        name: barn.name,
        id: barn.id,
        notificationSettings: tierSettings,
      });

      return acc;
    }, []);
  }, [barnOverridesData]);

  // --- Navigation/side effects ---
  useEffect(() => {
    setBarnOverrides(transformedBarnSettings);
  }, [transformedBarnSettings]);

  // --- Function definitions ---
  const handleAddOverride = async () => {
    if (!selectedBarnId) return;

    const selectedBarn = availableBarns.find((barn) => barn.id === selectedBarnId);
    if (!selectedBarn) return;

    // Build defaults from orgTierSettings array into keyed object by tierId
    const tierSettingsFromOrg = Array.isArray(orgTierSettings)
      ? orgTierSettings.reduce((acc, s) => {
          acc[s.tierId] = {
            name: s.name,
            description: s.description,
            sort_order: s.sort_order,
            faultSettings: s.faultSettings || {},
          };
          return acc;
        }, {})
      : {};

    // objects array of tierSettingsFromOrg
    const objects = (orgTierSettings || []).reduce((acc, tier) => {
      Object.entries(tier.faultSettings || {}).forEach(([code, setting]) => {
        acc.push({
          barn_id: selectedBarnId,
          notification_tier_id: tier.tierId,
          code: parseInt(code),
          enabled: setting.alert?.enabled,
          alert_threshold_seconds: alertHoursToThresholdSeconds(setting.alert?.hours, code),
          alert_threshold_type: setting.alert?.type,
          renotify_enabled: setting.reminders?.enabled,
          renotify_threshold_seconds: setting.reminders.hours * 3600,
          notify_on_resolved: setting.resolved?.enabled,
        });
      });
      return acc;
    }, []);

    try {
      await upsertBarnFaultSettings({ variables: { objects } });

      setBarnOverrides((prev) => [
        ...prev,
        {
          id: selectedBarnId,
          name: selectedBarn.name,
          notificationSettings: tierSettingsFromOrg,
        },
      ]);

      setSelectedBarnId('');
      showBarnOverrideSavedToast(`Barn override saved for ${selectedBarn.name}`);
    } catch (error) {
      console.error('Error adding barn override:', error);
      Sentry.captureException(error);
    }
  };

  const handleRemoveOverride = async (barnId) => {
    try {
      const barnFromQuery = barnOverridesData?.farm?.find((b) => b.id === barnId);
      const tierIds = [
        ...new Set(barnFromQuery?.fault_notification_settings?.map((setting) => setting.tier.id) || []),
      ];
      for (const tierId of tierIds) {
        await deleteFaultSettings({
          variables: {
            tier_id: tierId,
            barn_id: barnId,
          },
        });
      }

      setBarnOverrides((prev) => prev.filter((b) => b.id !== barnId));
      showBarnOverrideSavedToast('Barn override deleted');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error removing override:', error);
      Sentry.captureException(error);
      toast(`Error deleting barn override: ${error.message}`, { type: 'error' });
    }
  };

  const handleBarnTierSettingChange = async (newFaultSettings, tierId, barnId) => {
    const objects = Object.entries(newFaultSettings).map(([code, setting]) => ({
      notification_tier_id: tierId,
      code: parseInt(code, 10),
      enabled: setting.alert?.enabled,
      alert_threshold_seconds: alertHoursToThresholdSeconds(setting.alert?.hours, code),
      alert_threshold_type: setting.alert?.type,
      renotify_enabled: setting.reminders?.enabled,
      renotify_threshold_seconds: (setting.reminders?.hours || 0) * 3600,
      notify_on_resolved: setting.resolved?.enabled,
      barn_id: barnId,
    }));

    try {
      await upsertBarnFaultSettings({ variables: { objects } });
      showBarnOverrideSavedToast();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error saving barn tier settings:', error);
      Sentry.captureException(error);
    }

    setBarnOverrides((prev) =>
      prev.map((barn) => {
        if (barn.id !== barnId) return barn;
        const existingTier = barn.notificationSettings?.[tierId] || {};
        return {
          ...barn,
          notificationSettings: {
            ...barn.notificationSettings,
            [tierId]: {
              ...existingTier,
              faultSettings: newFaultSettings,
            },
          },
        };
      }),
    );
  };

  // --- Render ---
  if (barnsLoading || barnOverridesLoading) {
    return (
      <div className="OrgTierSettingsPage-section">
        <LoadingSkeleton style={{ width: '100%', height: '400px' }} />
      </div>
    );
  }

  if (barnsError || barnOverridesError) {
    return (
      <div className="OrgTierSettingsPage-section">
        <div>Error loading data: {barnsError?.message || barnOverridesError?.message}</div>
      </div>
    );
  }

  return (
    <div className="OrgTierSettingsPage-section">
      <Heading className="OrgTierSettingsPage-sectionTitle">Barn-Specific Overrides</Heading>
      <div className="OrgTierSettingsPage-headerDescription">
        Barns listed here use a custom notification policy instead of the organization defaults.
      </div>

      <div className="OrgTierSettingsPage-addOverride">
        <div className="OrgTierSettingsPage-addOverrideControls">
          <Select
            name="barn-selector"
            value={selectedBarnId}
            onChange={(e) => setSelectedBarnId(e.target.value)}
            className="OrgTierSettingsPage-barnSelector"
            defaultText="Select a barn"
            itemList={availableBarns
              .filter((barn) => !barnOverrides.some((o) => o.id === barn.id))
              .map((barn) => ({ name: barn.name, id: barn.id }))}
          />
          <Button
            variant="vivid"
            color="success"
            content="Override Organization Policy"
            onClick={handleAddOverride}
            disabled={!selectedBarnId}
          />
        </div>
      </div>

      <div className="OrgTierSettingsPage-barnOverrides">
        {barnOverrides.map((barn) => (
          <div key={barn.id} className="OrgTierSettingsPage-barnOverride">
            <div className="OrgTierSettingsPage-barnCard">
              {barn?.notificationSettings && (
                <>
                  <div className="OrgTierSettingsPage-barnCardHeader">
                    <div className="OrgTierSettingsPage-barnInfo">
                      <div className="OrgTierSettingsPage-barnName">{barn.name}</div>
                    </div>
                    <div className="OrgTierSettingsPage-barnActions">
                      <Button
                        variant="text"
                        color="danger"
                        content="Delete Override"
                        left={<TrashIcon />}
                        onClick={() => handleRemoveOverride(barn.id)}
                      />
                    </div>
                  </div>
                  <div className="OrgTierSettingsPage-barnCardContent">
                    <div className="OrgTierSettingsPage-tiersList">
                      {Object.entries(barn.notificationSettings).map(([tierId, tierData]) => {
                        const tierName = tierData.name || 'Unknown Tier';
                        return (
                          <div key={tierId} className="OrgTierSettingsPage-tierItem">
                            <TierSettingsPanel
                              tierName={tierName}
                              settings={tierData.faultSettings || {}}
                              onSettingChange={(newFaultSettings) => {
                                handleBarnTierSettingChange(newFaultSettings, tierId, barn.id);
                              }}
                              disabled={false}
                              showTitle={true}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

BarnSettingsOverrideList.propTypes = {
  organizationId: PropTypes.string,
  planId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  orgTierSettings: PropTypes.array,
};

export default BarnSettingsOverrideList;
